import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ClientSectionModel, ClientSignReqModel, GenericResModel, LetterSectionModel, SaveClientSignature } from 'countable@model';
import { ToastrService } from 'ngx-toastr';
import { map, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { ClientAssignedPartnerModel } from '../model/client/client-assigned-partner.model';
import { ClientAssignedTeamModel } from '../model/client/client-assigned-team.model';
import { ClientEditModel } from '../model/client/client-edit.model';
import { ClientExtraInfo } from '../model/client/client-extra-info.model';
import { ClientImportModel } from '../model/client/client-import.model';
import { ClientNamesModel } from '../model/client/client-names.model';
import { ClientModel } from '../model/client/client.model';
import { EntityTypeMap, EntityTypeModel } from '../model/client/entity-type.model';
import { ClientIntegrationInfoModel } from '../model/integration/client-integration-info.model';

import { StatusModel } from '../model/user/status.model';

@Injectable({
  providedIn: 'root'
})
export class ClientService {

  private static readonly GET_INDIVIDUAL_CLIENT = environment.apiV1 + '/individual-client';
  private static readonly GET_TYPE_OF_SUB_ENTITY = environment.apiV1 + '/sub-entity-type';
  private static readonly GET_USER_ACCESS_CLIENTS = environment.apiV1 + '/user-access-clients';
  private static readonly GET_RIGHT_SIDE_PARTNER_FOLDER = environment.apiV1 + '/right-side-partner-folder';
  private static readonly GET_ALL_CLIENT_FILE = environment.apiV1 + '/all-client-file';
  private static readonly GET_CLIENT_VIEW_SUB_MODULES = environment.apiV1 + '/view-sub-modules';
  private static readonly GET_CLIENT_REQ_RES = environment.apiV1 + '/req-res';
  private static readonly GET_ALL_CLIENT_AVAILABLE_FILE = environment.apiV1 + '/all-available-file';
  private static readonly CLIENT = environment.apiV1 + '/client';
  private static readonly CLIENT_LIST_BASIC_INFO = environment.apiV1 + '/client/list';
  private static readonly UPDATE_ALL_CLIENT_MESSAGE = environment.apiV1 + '/all-message';
  private static readonly REMOVE_ALL_CLIENT_FILES = environment.apiV1 + '/remove-all-file';
  private static readonly SAVE_ENG_DOC_RESPONSE = environment.apiV1 + '/save-eng-doc-response';
  private static readonly SAVE_CSV_CLIENT_DATA = environment.apiV1 + '/save-csv-data';
  private static readonly VALIDATE_CLIENT_ENTITY_NAME = environment.apiV1 + '/validate-entity-name';
  private static readonly VALIDATE_CLIENT_IDS = environment.apiV1 + '/validate-client-ids';
  private static readonly SAVE_CLIENT_SIGNATURE = environment.apiV1 + '/client-signature';
  private static readonly UPDATE_CLIENT_PROCESS_STATUS = environment.apiV1 + '/client-process-status';
  private static readonly REMOVE_ENG_FROM_CLIENT_INFO = environment.apiV1 + '/remove-eng-client-info';
  private static readonly GET_ALL_ENTITY_TYPES = environment.apiV1 + '/client/entity-type';
  private static readonly GET_CLIENT_INTEGRATION_INFO = environment.apiV1 + '/client/integrations';
  private static readonly GET_CLIENT_EMAILS = environment.apiV1 + '/client/emails';
  private static readonly GET_CLIENT_PHONES = environment.apiV1 + '/client/phones';
  private static readonly GET_CLIENT_ENS_COUNT = environment.apiV1 + '/client/eng-count';
  private static readonly GET_CLIENT_STATUSES = environment.apiV1 + '/client/statuses';
  private static readonly GET_CLIENT_ASSIGNED = environment.apiV1 + '/client/assigned';
  private static readonly GET_CLIENT_ASSIGNED_PARTNERS = environment.apiV1 + '/client/assigned-partners';
  private static readonly GET_CLIENT_ASSIGNED_TEAMS = environment.apiV1 + '/client/assigned-teams';
  private static readonly URI_CLIENT_INVITE = environment.apiV1 + '/client/invite';
  private static readonly REMOVE_CLIENT_QUESTION = environment.apiV1 + '/remove-client-question';
  private static readonly SAVE_CLIENT_QUESTION_ANSWER = environment.apiV1 + '/remove-client-question-answer';
  private static readonly CLIENT_IMPORT_BY_CSV = environment.apiV1 + '/client/import';
  private static readonly SAVE_CLIENT_VALIDATE_TITLE = environment.apiV1 + '/client/validate-title';
  private static readonly SAVE_CLIENT_VALIDATE_ENTITY_NAME = environment.apiV1 + '/client/validate-entity';
  private static readonly URI_CLIENT_ASSIGN_UNASSIGN_PARTNER = environment.apiV1 + '/client/assign-partner';
  private static readonly URI_CLIENT_ASSIGN_UNASSIGN_TEAM = environment.apiV1 + '/client/assign-team';
  private static readonly URI_CLIENT_ENG_COUNT_ACTIVE = environment.apiV1 + '/client/eng-count-active';
  private static readonly URI_CLIENT_CHANGE_STATUS = environment.apiV1 + '/client/change-status';

  public readonly selectedClient: ReplaySubject<ClientModel> = new ReplaySubject<ClientModel>(1);
  public readonly teamClientRightPanelStatus = new Subject<boolean>();
  public readonly openClientRepository = new Subject<{
    isOpenRepository: boolean,
    clientUser: number,
    entityName: string,
    clientFirm: number
  }>();

  private entityTypes: EntityTypeMap = null;

  constructor(private http: HttpClient, private toaster: ToastrService) {
  }

  getAllIntegrationsInfo(): Observable<Map<number, ClientIntegrationInfoModel>> {
    return this.http.get<GenericResModel<ClientIntegrationInfoModel[]>>(ClientService.GET_CLIENT_INTEGRATION_INFO).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i]));
        }
        return null;
      })
    );
  }

  getAllAssignedClients(): Observable<Set<number>> {
    return this.http.get<GenericResModel<number[]>>(ClientService.GET_CLIENT_ASSIGNED).pipe(
      map(res => {
        if (res.status == 200) {
          return new Set(res.data);
        }
        return null;
      })
    );
  }

  getCurrentAssignedPartners(): Observable<Map<number, ClientAssignedPartnerModel>> {
    return this.http.get<GenericResModel<ClientAssignedPartnerModel[]>>(ClientService.GET_CLIENT_ASSIGNED_PARTNERS).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i]));
        }
        return null;
      })
    );
  }

  getCurrentAssignedTeamsInfo(): Observable<Map<number, ClientAssignedTeamModel>> {
    return this.http.get<GenericResModel<ClientAssignedTeamModel[]>>(ClientService.GET_CLIENT_ASSIGNED_TEAMS).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i]));
        }
        return null;
      })
    );

  }

  getEngCounts(): Observable<Map<number, number>> {
    return this.http.get<GenericResModel<Map<number, number>>>(ClientService.GET_CLIENT_ENS_COUNT).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(Object.entries(res.data).map(([key, value]) => [Number(key), Number(value)]));
        }
        return null;
      })
    );
  }

  getClientsStatuses(): Observable<Map<number, StatusModel>> {
    return this.http.get<GenericResModel<StatusModel[]>>(ClientService.GET_CLIENT_STATUSES).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i]));
        }
        return null;
      })
    );
  }

  getAllClientsEmail(): Observable<Map<number, string>> {
    return this.http.get<GenericResModel<any>>(ClientService.GET_CLIENT_EMAILS).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i.email]));
        }
        return null;
      })
    );
  }

  getAllClientsPhone(): Observable<Map<number, string>> {
    return this.http.get<GenericResModel<any>>(ClientService.GET_CLIENT_PHONES).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i.phone]));
        }
        return null;
      })
    );
  }

  getAllEntityType(refresh: boolean = false): Observable<EntityTypeMap> {
    if (this.entityTypes && this.entityTypes.types.size > 0 && !refresh) {
      return of(this.entityTypes);
    } else {
      return this.http.get<GenericResModel<EntityTypeModel>>(ClientService.GET_ALL_ENTITY_TYPES).pipe(
        map(res => {
          if (res.status == 200) {
            this.entityTypes = new EntityTypeMap();
            res.data.types.forEach(i => {
              this.entityTypes.types.set(i.id, i);
            });
            res.data.subTypes.forEach(i => {
              this.entityTypes.subTypes.set(i.id, i);
            });
            return this.entityTypes;
          } else {
            res.errors.forEach(err => this.toaster.warning(err));
            return new EntityTypeMap();
          }
        })
      );
    }
  }

  getAllClientsBasicInfo(): Observable<Map<number, ClientModel>> {
    return this.http.get<GenericResModel<ClientModel[]>>(ClientService.CLIENT_LIST_BASIC_INFO).pipe(
      map(res => {
        if (res.status == 200) {
          return new Map(res.data.map(i => [i.clientFirm, i]));
        } else {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return null;
      })
    );
  }

  saveClient(stringData: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(ClientService.CLIENT, stringData);
  }

  validateTitle(legalName: string): Observable<GenericResModel<void>> {
    return this.http.post<GenericResModel<void>>(ClientService.SAVE_CLIENT_VALIDATE_TITLE, legalName);
  }

  validateEntityName(entityName: string): Observable<GenericResModel<void>> {
    return this.http.post<GenericResModel<void>>(ClientService.SAVE_CLIENT_VALIDATE_ENTITY_NAME, entityName);
  }

  updateClient(clientData: ClientEditModel): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(ClientService.CLIENT, clientData);
  }

  deleteClient(data: { clientFirm: number, isArchived: boolean }): Observable<GenericResModel<void>> {
    return this.http.delete<GenericResModel<void>>(ClientService.CLIENT, {body: data});
  }

  getClientEngCountActive(clientFirm: number): Observable<GenericResModel<number>> {
    return this.http.get<GenericResModel<number>>(ClientService.URI_CLIENT_ENG_COUNT_ACTIVE, {params: {'client': clientFirm}});
  }

  getIndividualClient(clientFirmId: number): Observable<any> {
    return this.http.get(ClientService.GET_INDIVIDUAL_CLIENT, {params: {'c': clientFirmId}})
      .pipe(tap(res => {
        if (res) {
          localStorage.setItem('selectedClientDetails', JSON.stringify(res.clientdata));
        }
      }));
  }

  getClientNameForEng(): Observable<any> {
    return this.http.get(environment.apiV1 + '/client-name-for-eng');
  }

  getClientNames(): Observable<GenericResModel<ClientNamesModel[]>> {
    return this.http.get<GenericResModel<ClientNamesModel[]>>(environment.apiV1 + '/client/names');
  }

  getDashboardClientCount(): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(environment.apiV1 + '/dashboard-client-cnt');
  }

  /**
   * @Deprecated Use getAllEntityType instead
   */
  getTypeOfSubEntity(): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_TYPE_OF_SUB_ENTITY).pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  getUserClientAccess(userAccountId: number, userAccessTypeId: number): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_USER_ACCESS_CLIENTS,
      {params: {'userAccountId': userAccountId, 'userAccessTypeId': userAccessTypeId}})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  getRightSidePartnerFolder(partnerUserAccountId: number, clientUserAccountId: number, type, clientFirmId: number, searchText: string): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_RIGHT_SIDE_PARTNER_FOLDER, {
      params: {
        'partnerUserAccountId': partnerUserAccountId, 'clientUserAccountId': clientUserAccountId,
        'type': type, 'clientFirmId': clientFirmId, 'searchText': searchText
      }
    });
  }

  getAllClientFile(orderType: number, searchTxt: string, clientFirmId: number): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_ALL_CLIENT_FILE,
      {params: {'orderType': orderType, 'searchTxt': searchTxt, 'clientFirmId': clientFirmId}})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  getClientViewSubModules(engId: number): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_CLIENT_VIEW_SUB_MODULES, {params: {'engId': engId}})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  getClientReqRes(engId, subModuleId, orderType, searchTxt, isOverDue, isPending): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_CLIENT_REQ_RES, {
      params: {
        'engId': engId, 'subModuleId': subModuleId, 'orderType': orderType, 'searchTxt': searchTxt,
        'isOverDue': isOverDue, 'isPending': isPending
      }
    }).pipe(
      map(res => {
        if (res.status !== 200 && res.status !== 204) {
          res.errors.forEach(err => this.toaster.warning(err));
        }
        return res.data;
      })
    );
  }

  getAllClientAvailableFile(engId, subModuleId, orderType, searchTxt): Observable<GenericResModel<string>> {
    return this.http.get<GenericResModel<string>>(ClientService.GET_ALL_CLIENT_AVAILABLE_FILE,
      {params: {'engId': engId, 'subModuleId': subModuleId, 'orderType': orderType, 'searchTxt': searchTxt}});
  }

  updateAllClientMessage(stringData: string): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(ClientService.UPDATE_ALL_CLIENT_MESSAGE, stringData);
  }

  deleteAllClientFiles(stringData: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(ClientService.REMOVE_ALL_CLIENT_FILES, {params: {'stringData': stringData}});
  }

  saveEngDocResponse(stringData: string): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(ClientService.SAVE_ENG_DOC_RESPONSE, stringData);
  }

  saveClientCsvData(stringData: string): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(ClientService.SAVE_CSV_CLIENT_DATA, stringData);
  }

  getClientExtraInfo(clientFirm: number): Observable<GenericResModel<ClientExtraInfo>> {
    return this.http.get<GenericResModel<ClientExtraInfo>>(environment.apiV1 + '/client/get-extra-info', {params: {'client': clientFirm}});
  }

  validateClientEntityName(entityName: string, emailAddress: string): Observable<string> {
    return this.http.get<GenericResModel<string>>(ClientService.VALIDATE_CLIENT_ENTITY_NAME,
      {params: {'entityName': entityName, 'emailAddress': emailAddress}})
      .pipe(
        map(res => {
          if (res.status !== 200 && res.status !== 204) {
            res.errors.forEach(err => this.toaster.warning(err));
          }
          return res.data;
        })
      );
  }

  validateClientIds(data: string): Observable<GenericResModel<string>> {
    return this.http.post<GenericResModel<string>>(ClientService.VALIDATE_CLIENT_IDS, data);
  }

  saveClientSignature(saveClientSignature: SaveClientSignature): Observable<GenericResModel<number>> {
    return this.http.post<GenericResModel<number>>(ClientService.SAVE_CLIENT_SIGNATURE, saveClientSignature);
  }

  updateProcessStatus(processStatus: number): Observable<GenericResModel<string>> {
    let params = new HttpParams();
    params = params.set('status', processStatus);
    return this.http.put<GenericResModel<string>>(ClientService.UPDATE_CLIENT_PROCESS_STATUS, {}, {params});
  }

  deleteEngFromClientInfo(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(ClientService.REMOVE_ENG_FROM_CLIENT_INFO, {params: {'data': data}});
  }

  invite(mode: 'WEB' | 'MOBILE' = 'WEB', clientFirmIds: number[]): Observable<GenericResModel<number>> {
    return this.http.put<GenericResModel<number>>(ClientService.URI_CLIENT_INVITE, clientFirmIds, {headers: {'X-MODE': mode}});
  }

  deleteClientQuestion(data: string): Observable<GenericResModel<boolean>> {
    return this.http.delete<GenericResModel<boolean>>(ClientService.REMOVE_CLIENT_QUESTION, {params: {'data': data}});
  }

  saveClientQuestionAnswer(data: string): Observable<GenericResModel<boolean>> {
    return this.http.post<GenericResModel<boolean>>(ClientService.SAVE_CLIENT_QUESTION_ANSWER, data);
  }

  partnerAssignUnAssign(data: { clientFirm: number[], firmPartner: number }): Observable<GenericResModel<void>> {
    return this.http.post<GenericResModel<void>>(ClientService.URI_CLIENT_ASSIGN_UNASSIGN_PARTNER, data);
  }

  teamAssignUnAssign(data: {
    clientFirm: number[],
    firmPartner: number,
    firmStaff: number,
    role: number,
    firmUser: number
  }): Observable<GenericResModel<void>> {
    return this.http.post<GenericResModel<void>>(ClientService.URI_CLIENT_ASSIGN_UNASSIGN_TEAM, data);
  }

  changeClientArchiveStatus(clientFirmId: any, isArchived: boolean): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(ClientService.URI_CLIENT_CHANGE_STATUS, clientFirmId, {params: {'archive': isArchived}});
  }

  importClientsByCsv(data: ClientImportModel[]): Observable<GenericResModel<boolean>> {
    return this.http.put<GenericResModel<boolean>>(ClientService.CLIENT_IMPORT_BY_CSV, data);
  }

  public fetchEngLetterSections(engagementId: number): Observable<GenericResModel<ClientSectionModel[]>> {
    return this.http.get<GenericResModel<ClientSectionModel[]>>(environment.apiV2 + `/client-portal/eng/sections/${engagementId}`);
  }

  public fetchLetterBySection(engagementId: number,subModuleId: number):Observable<GenericResModel<LetterSectionModel>> {
    return this.http.get<GenericResModel<LetterSectionModel>>(environment.apiV2 + `/client-portal/eng/section/${subModuleId}/${engagementId}`);
  }

  public createSignature(formData: FormData,engagementId: number, subModuleId: number,id: number): Observable<GenericResModel<boolean>> {
    const uri = environment.apiV2 + `/client-portal/eng/signature-image/${subModuleId}/${id}/${engagementId}`;
    return this.http.post<GenericResModel<boolean>>(uri,formData)
  }

  public addDrawSignature(engagementId: number, subModuleId: number,input: ClientSignReqModel) {
    const uri = environment.apiV2 + `/client-portal/eng/signature/${subModuleId}/${engagementId}`;
    return this.http.post<GenericResModel<boolean>>(uri,input)
  }

  public acceptAndSignoff(engagementId: number, subModuleId: number,id: number) {
    const uri = environment.apiV2 + `/client-portal/eng/signature-image/${subModuleId}/${id}/${engagementId}`;
    return this.http.put<GenericResModel<boolean>>(uri,null)
  }
}
